import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heading } from '../catalyst/heading';
import { ArrowLeft } from 'lucide-react';
import { useUser } from '../hooks/useUsers';
import { useConversationMessages, useToolEvents } from '../hooks/useConversation';
import type { TimelineItem, ToolEvent } from '../types/database';
import ChatTimeline from '../components/ChatTimeline';
import ChatInput from '../components/ChatInput';


const ChatDetailPage: React.FC = () => {
	const { userId } = useParams<{ userId: string }>();
	const { data: user, loading: userLoading } = useUser(userId);
	const {
		data: messages,
		loading: messagesLoading,
		error: messagesError,
		refetch: refetchMessages,
	} = useConversationMessages(userId);
	const {
		data: toolEvents,
		loading: toolEventsLoading,
		refetch: refetchToolEvents,
	} = useToolEvents(userId);


	const loading = userLoading || messagesLoading || toolEventsLoading;

	const timelineItems = useMemo<TimelineItem[]>(() => {
		if (!messages && !toolEvents) return [];

		const items: TimelineItem[] = [];

		// Add all messages
		for (const msg of messages ?? []) {
			items.push({ kind: 'message', data: msg });
		}

		// Group tool events by tool_call_id: pair CALL with RESULT
		const callMap = new Map<string, ToolEvent>();
		const resultMap = new Map<string, ToolEvent>();

		for (const evt of toolEvents ?? []) {
			if (evt.event_type === 'CALL') {
				callMap.set(evt.tool_call_id, evt);
			} else {
				resultMap.set(evt.tool_call_id, evt);
			}
		}

		// For each call, create a tool_call item (with optional result)
		const pairedResultIds = new Set<string>();
		callMap.forEach((callEvt, toolCallId) => {
			const resultEvt = resultMap.get(toolCallId);
			if (resultEvt) pairedResultIds.add(resultEvt.id);
			items.push({ kind: 'tool_call', data: callEvt, result: resultEvt });
		});
		// Standalone results (no matching call) — shouldn't happen normally, but handle gracefully
		resultMap.forEach((resultEvt) => {
			if (!pairedResultIds.has(resultEvt.id)) {
				items.push({ kind: 'tool_result', data: resultEvt });
			}
		});

		// Sort by created_at
		items.sort(
			(a, b) =>
				new Date(a.data.created_at).getTime() - new Date(b.data.created_at).getTime()
		);

		return items;
	}, [messages, toolEvents]);

	const handleSent = () => {
		refetchMessages();
		refetchToolEvents();
	};

	const displayName = user?.display_name || user?.phone_number || userId || 'User';

	return (
		<div className="flex h-[calc(100vh-8rem)] min-h-0">
			{/* Main chat area */}
			<div className="flex flex-1 flex-col overflow-hidden">
				{/* Header */}
				<div className="border-b border-zinc-200 px-6 py-4 dark:border-zinc-700">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Link
								to="/chats"
								className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
							>
								<ArrowLeft className="h-5 w-5" />
							</Link>
							<div>
								<Heading level={2}>{displayName}</Heading>
								{user && user.display_name && (
									<p className="text-sm text-zinc-500">{user.phone_number}</p>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Timeline */}
				<div className="flex-1 overflow-y-auto p-4">
					{loading && (
						<div className="flex h-full items-center justify-center">
							<p className="text-sm text-zinc-500">Loading conversation…</p>
						</div>
					)}

					{messagesError && (
						<div className="flex h-full items-center justify-center">
							<p className="text-sm text-red-600 dark:text-red-400">{messagesError}</p>
						</div>
					)}

					{!loading && !messagesError && <ChatTimeline items={timelineItems} />}
				</div>

				{/* Input */}
				{userId && (
					<div className="border-t border-zinc-200 p-4 dark:border-zinc-700">
						<ChatInput userId={userId} onSent={handleSent} />
					</div>
				)}
			</div>
		</div>
	);
};

export default ChatDetailPage;
